import React, { useState } from "react";
import { Button, Tag } from "../../../../../design-system";
import mediaOps, { AccountSummary, DeliverySummary, TargetSummary } from "../../../../../database/mediaOps/mediaOpsApi";
import { formatInZone } from "../../../../../database/mediaOps/zonedTime";
import OwnerPackPanel from "./OwnerPackPanel";
import { useMutation, useOpRead } from "./hooks";
import { markedPostedLabel, modeLabel, publicationSource } from "./stateLabels";
import {
  ErrorLine,
  FieldLabel,
  FormGrid,
  Grow,
  ListRow,
  Mono,
  Panel,
  PanelHead,
  ResultNotice,
  Row,
  Select,
  Small,
  TextInput,
  StateChips,
  Table,
  TableScroll,
  fmtTime,
} from "./ui";

// Deliveries: one row per schedule, one block per target. Schedule, Delivery
// and Publication stay separate chips. Cancel, retry and reconcile report per
// target which ones were not acted on and why.

const SCHEDULE_FILTERS = ["", "scheduled", "dispatching", "completed", "blocked", "cancelled", "cancellation_requested"];

export const DeliveriesView: React.FC<{ communityId: string }> = ({ communityId }) => {
  const accountsRead = useOpRead(() => mediaOps.listAccounts(communityId), [communityId]);
  const accounts: AccountSummary[] = accountsRead.result?.data?.accounts || [];
  const [accountId, setAccountId] = useState("");
  const [state, setState] = useState("");
  const list = useOpRead(
    () => mediaOps.listDeliveries(communityId, { ...(accountId ? { accountId } : {}), ...(state ? { states: [state] } : {}) }),
    [communityId, accountId, state]
  );
  const deliveries: DeliverySummary[] = list.result?.data?.deliveries || [];
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <Panel>
      <PanelHead>
        Deliveries <Grow />
        <Button size="sm" variant="secondary" onClick={list.reload}>
          Refresh
        </Button>
      </PanelHead>
      <FormGrid>
        <FieldLabel>
          Account
          <Select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">All accounts</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                @{a.handle} ({a.platform})
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel>
          Schedule state
          <Select value={state} onChange={(e) => setState(e.target.value)}>
            {SCHEDULE_FILTERS.map((s) => (
              <option key={s} value={s}>
                {s || "Any state"}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </FormGrid>
      {list.result?.status === "blocked" ? <ResultNotice result={list.result} /> : null}
      {deliveries.length === 0 && list.result?.status !== "blocked" ? <Small>No deliveries match.</Small> : null}
      {deliveries.map((d) => (
        <ListRow key={d.scheduleId} $active={openId === d.scheduleId} style={{ flexDirection: "column", alignItems: "stretch" }}>
          <Row>
            <Mono>{d.scheduleId}</Mono>
            <Small>Project {d.projectId}</Small>
            <Small>Revision {d.revisionId}</Small>
            <Grow />
            <Button size="sm" variant="secondary" onClick={() => setOpenId(openId === d.scheduleId ? null : d.scheduleId)}>
              {openId === d.scheduleId ? "Close" : "Details"}
            </Button>
          </Row>
          {openId === d.scheduleId ? (
            <ScheduleDetail communityId={communityId} scheduleId={d.scheduleId} onChanged={list.reload} />
          ) : (
            d.targets.map((t) => <TargetLine key={t.targetId} target={t} />)
          )}
        </ListRow>
      ))}
    </Panel>
  );
};

const TargetLine: React.FC<{ target: TargetSummary }> = ({ target: t }) => (
  <Row>
    <strong>@{t.handle || t.accountId}</strong>
    <Tag size="sm">{modeLabel(t.deliveryMode)}</Tag>
    <StateChips states={t.states} mode={t.deliveryMode} publicationSource={publicationSource(t.publicationEvidence)} />
    <Small>{formatInZone(t.intendedPublishAt, t.timezone)}</Small>
  </Row>
);

type PerTargetResult = { targetId: string; accountId?: string; result?: string; retried?: boolean; reason?: string; note?: string };

const ScheduleDetail: React.FC<{ communityId: string; scheduleId: string; onChanged: () => void }> = ({ communityId, scheduleId, onChanged }) => {
  const detail = useOpRead(() => mediaOps.getDelivery(communityId, scheduleId), [communityId, scheduleId]);
  const action = useMutation<{ targets?: PerTargetResult[] }>();
  const [packId, setPackId] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const d = detail.result?.data;
  if (!d) return detail.result ? <ResultNotice result={detail.result} /> : <Small>Loading.</Small>;

  const act = async (kind: "cancel" | "retry" | "reconcile", targetIds?: string[]) => {
    const base = { scheduleId, ...(targetIds ? { targetIds } : {}) };
    // One idempotency key per (action, targets): retrying one target after a
    // network error never reuses the key of another target or action.
    const scope = `${kind}:${targetIds ? targetIds.join(",") : "all"}`;
    const res = await action.run(
      (k) =>
        kind === "cancel"
          ? mediaOps.cancelDelivery(k, communityId, { ...base, expectedVersion: d.version })
          : kind === "retry"
          ? mediaOps.retryDelivery(k, communityId, { ...base, expectedVersion: d.version })
          : mediaOps.reconcileDelivery(k, communityId, base),
      scope
    );
    if (res && !res.transportError) {
      detail.reload();
      onChanged();
    }
  };
  const perTarget = action.result?.data?.targets || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Row>
        <Button size="sm" variant="secondary" onClick={() => act("retry")} isLoading={action.busy}>
          Retry failed targets
        </Button>
        <Button size="sm" variant="secondary" onClick={() => act("reconcile")} isLoading={action.busy}>
          Reconcile unknown outcomes
        </Button>
        <Button
          size="sm"
          variant="danger"
          onClick={() => {
            if (!confirmCancel) {
              setConfirmCancel(true);
              return;
            }
            setConfirmCancel(false);
            act("cancel");
          }}
          isLoading={action.busy}
        >
          {confirmCancel ? "Confirm cancel all" : "Cancel all targets"}
        </Button>
      </Row>
      <ResultNotice result={action.result} />
      {perTarget.length > 0 ? (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <th>Target</th>
                <th>Outcome</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {perTarget.map((r) => (
                <tr key={r.targetId}>
                  <td>{r.accountId || r.targetId}</td>
                  <td>{r.retried === false ? "Not retried" : r.retried ? "Retried" : r.result || ""}</td>
                  <td>{[r.reason, r.note].filter(Boolean).join(". ")}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      ) : null}
      {d.targets.map((t) => (
        <Panel key={t.targetId}>
          <Row>
            <strong>@{t.handle || t.accountId}</strong>
            <Tag size="sm">{modeLabel(t.deliveryMode)}</Tag>
            <Grow />
            <Mono>{t.targetId}</Mono>
          </Row>
          <StateChips states={t.states} mode={t.deliveryMode} publicationSource={publicationSource(t.publicationEvidence)} />
          <Small>
            Intended publish {formatInZone(t.intendedPublishAt, t.timezone)}. Dispatch {formatInZone(t.dispatchAt, t.timezone)}.
          </Small>
          {t.lastError ? <ErrorLine error={t.lastError} /> : null}
          {t.cancellationNote ? <Small>{t.cancellationNote}</Small> : null}
          {t.publicationEvidence?.publishedAt ? (
            <Small>
              {t.states.publication === "provider_verified"
                ? "Verified by the platform"
                : `${markedPostedLabel(publicationSource(t.publicationEvidence))}, not verified by the platform`}
              . Published {fmtTime(t.publicationEvidence.publishedAt)}
              {t.publicationEvidence.publishedUrl ? ` at ${t.publicationEvidence.publishedUrl}` : ""}.
            </Small>
          ) : null}
          {["owner_reported", "provider_verified"].includes(t.states.publication) ? <MetricsRefreshAction communityId={communityId} scheduleId={scheduleId} target={t} /> : null}
          {t.states.publication === "owner_reported" && t.publicationEvidence?.publishedAt && !t.publicationEvidence?.publishedUrl ? <PublicationUrlAction communityId={communityId} scheduleId={scheduleId} target={t} onChanged={detail.reload} /> : null}
          {t.attempts && t.attempts.length > 0 ? (
            <TableScroll>
              <Table>
                <thead>
                  <tr>
                    <th>Phase</th>
                    <th>Provider</th>
                    <th>Time</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {t.attempts.map((a) => (
                    <tr key={a.attemptId}>
                      <td>
                        {a.phase}
                        {a.outcome ? ` (${a.outcome})` : ""}
                      </td>
                      <td>{a.provider}</td>
                      <td>{fmtTime(a.updatedAt || a.createdAt)}</td>
                      <td>{a.error ? `${a.error.code}: ${a.error.message}` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableScroll>
          ) : (
            <Small>No attempts yet.</Small>
          )}
          <Row>
            <Button size="sm" variant="ghost" onClick={() => act("retry", [t.targetId])} disabled={action.busy}>
              Retry
            </Button>
            <Button size="sm" variant="ghost" onClick={() => act("reconcile", [t.targetId])} disabled={action.busy}>
              Reconcile
            </Button>
            <Button size="sm" variant="ghost" onClick={() => act("cancel", [t.targetId])} disabled={action.busy}>
              Cancel
            </Button>
            {t.handoffId ? (
              <Button size="sm" variant="secondary" onClick={() => setPackId(packId === t.handoffId ? null : t.handoffId)}>
                {packId === t.handoffId ? "Hide owner pack" : "Owner pack"}
              </Button>
            ) : (
              <Small>No owner pack yet. It exists after dispatch.</Small>
            )}
          </Row>
          {packId && packId === t.handoffId ? <OwnerPackPanel communityId={communityId} handoffId={packId} onChanged={detail.reload} /> : null}
        </Panel>
      ))}
    </div>
  );
};

const PublicationUrlAction: React.FC<{ communityId: string; scheduleId: string; target: TargetSummary; onChanged: () => void }> = ({ communityId, scheduleId, target, onChanged }) => {
  const [url, setUrl] = useState("");
  const action = useMutation();
  const submit = async () => {
    const res = await action.run((k) => mediaOps.setPublicationUrl(k, communityId, { scheduleId, targetId: target.targetId, publishedAt: target.publicationEvidence!.publishedAt!, publishedUrl: url.trim() }));
    if (res && res.status !== "blocked") onChanged();
  };
  return <div><Row><TextInput type="url" placeholder="Public post URL (https://)" value={url} onChange={(e) => { setUrl(e.target.value); action.edited(); }} /><Button size="sm" variant="secondary" onClick={submit} disabled={!/^https:\/\//i.test(url.trim())} isLoading={action.busy}>Save post URL</Button></Row><ResultNotice result={action.result} okText="Public URL added to the existing owner report." /></div>;
};

const MetricsRefreshAction: React.FC<{ communityId: string; scheduleId: string; target: TargetSummary }> = ({ communityId, scheduleId, target }) => {
  const action = useMutation<{ jobId?: string; unsupported?: { message: string }[] }>();
  const [jobId, setJobId] = useState("");
  const job = useOpRead(() => jobId ? mediaOps.getJob(communityId, jobId) : null, [communityId, jobId]);
  const submit = async () => {
    const res = await action.run((k) => mediaOps.refreshPostMetrics(k, communityId, { targets: [{ deliveryId: scheduleId, targetId: target.targetId }] }));
    if (res?.data?.jobId) setJobId(res.data.jobId);
  };
  return <div>
    <Row>
      <Button size="sm" variant="secondary" onClick={submit} disabled={!target.publicationEvidence?.publishedUrl} isLoading={action.busy}>Refresh post metrics</Button>
      {!target.publicationEvidence?.publishedUrl ? <Small>Add a public post URL to the publication evidence to read metrics.</Small> : null}
      {jobId ? <Button size="sm" variant="ghost" onClick={job.reload}>Refresh result</Button> : null}
    </Row>
    <ResultNotice result={action.result} okText={jobId ? `Metrics job ${jobId} queued.` : "Metrics request recorded."} />
    {job.result?.data ? <Small>Metrics job {job.result.data.status}: {JSON.stringify(job.result.data.diagnostics?.targets || job.result.data.diagnostics?.failed || [])}</Small> : null}
  </div>;
};

export default DeliveriesView;
